import React, { useEffect, useId, useRef, useState } from 'react';
import {
    countReasonsLength,
    getDisplayedReasons,
    readReasonsOverride,
    REASONS_MAX_LENGTH,
    validateReasonsInput,
} from './reasonsOverride';

// §4.4 の「おすすめポイント」の区分で、出す文が無いとき (AI の生成に失敗し、編集した文も無い)
export const REASONS_NOT_GENERATED_TEXT = 'おすすめポイントを生成できませんでした。';

/**
 * 推薦カードのおすすめポイント (DP-M-005) と、その文の編集 (IN-M-012・BTN-M-015〜018・DP-M-019)。
 * 画面レイアウト_SCR-M-001.md §4.4・§6.2「おすすめポイントの文の編集」・§7.2、Issue #777。
 *
 * 出す文は、編集した文があればその文、無ければ AI の文 (推薦 PDF と同じ決め方。PBI-002 §11.11)。
 * 「マッチ度を表示する」(IN-M-009) を外していても出す (隠れるのはマッチ度だけ)。
 *
 * 編集中かどうか・保存中かどうか・保存の失敗の文言は、呼び出し元 (useReasonOverrides) が持つ
 * (編集できる場所を画面全体で 1 つにし、保存を重ねて送らないため)。入力欄の文と入力の誤りは本部品が持つ
 * (RecommendationScore と同じ分け方)。
 *
 * @param {object} props
 * @param {object} props.item 推薦の要素 (reasons・reasons_override)
 * @param {boolean} props.editing このカードの文を編集中か
 * @param {boolean} props.saving このカードの文を保存中か
 * @param {boolean} props.locked 「編集」「AI の文に戻す」を押せないか (ほかの編集中・保存中)
 * @param {boolean} props.saveLocked 編集中の「保存」を押せないか (ほかの保存中。§4.3 BTN-M-016)
 * @param {string|null} props.errorMessage このカードの保存の失敗の文言
 * @param {() => void} props.onStartEdit
 * @param {() => void} props.onCancelEdit
 * @param {(text: string|null) => Promise<boolean>} props.onSave null は「AI の文に戻す」
 * @param {() => void} props.onClearError
 */
function RecommendationReasons({
    item,
    editing = false,
    saving = false,
    locked = false,
    saveLocked = false,
    errorMessage = null,
    onStartEdit,
    onCancelEdit,
    onSave,
    onClearError,
}) {
    const displayed = getDisplayedReasons(item);
    const override = readReasonsOverride(item);

    const [text, setText] = useState('');
    const [inputError, setInputError] = useState(null);
    const textareaRef = useRef(null);
    const editButtonRef = useRef(null);
    const wasEditingRef = useRef(false);
    const focusEditAfterRevertRef = useRef(false);
    const counterId = useId();

    // 編集を始めたら、今表示している文を入力欄に入れてフォーカスを移す (カーソルは末尾)。
    // 編集を終えたら (保存できた・キャンセルした)、フォーカスを「編集」に戻す (§7.2)
    useEffect(() => {
        if (editing && !wasEditingRef.current) {
            const initial = displayed ?? '';
            setText(initial);
            setInputError(null);
            const textarea = textareaRef.current;
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(initial.length, initial.length);
            }
        } else if (!editing && wasEditingRef.current) {
            // 入力の誤りは編集中だけのもの (残すと、編集を終えた後の表示に出てしまう)
            setInputError(null);
            editButtonRef.current?.focus();
        }
        wasEditingRef.current = editing;
        // displayed は編集を始めた時点の値だけを使う (編集中に変わっても入力欄を上書きしない)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    // 「AI の文に戻す」が保存できた後のフォーカスの移動 (RecommendationScore と同じ。押せる表示に
    // なった後の描画でも試す)
    const focusEditButtonIfPending = () => {
        const button = editButtonRef.current;
        if (!focusEditAfterRevertRef.current || !button || button.disabled) return;
        focusEditAfterRevertRef.current = false;
        button.focus();
    };
    useEffect(focusEditButtonIfPending);

    const handleChange = (event) => {
        setText(event.target.value);
        setInputError(null);
        onClearError?.();
    };

    const handleSave = () => {
        if (saving || saveLocked) return;
        const checked = validateReasonsInput(text);
        if (!checked.ok) {
            setInputError(checked.message);
            return;
        }
        // 今表示している文 (前後の空白を除いたもの) と同じなら、保存を呼ばずに編集を終える (§6.2 手順 3)
        if (checked.value === (displayed ?? '').trim()) {
            onCancelEdit?.();
            return;
        }
        onSave?.(checked.value);
    };

    const handleKeyDown = (event) => {
        // Enter は改行のまま (保存は「保存」ボタン)。Esc でキャンセルする (§6.2 手順 7)
        if (event.key === 'Escape' && !saving) {
            event.preventDefault();
            onCancelEdit?.();
        }
    };

    // AI の文に戻す (BTN-M-018)。保存できるとこのボタンは消えるため、フォーカスを「編集」に移す (§7.2)
    const handleRevert = async () => {
        const saved = await onSave?.(null);
        if (saved) {
            focusEditAfterRevertRef.current = true;
            focusEditButtonIfPending();
        }
    };

    const alertMessage = inputError || errorMessage;

    return (
        <div className="recommendation-section">
            <div className="recommendation-reasons-heading">
                <h5 className="recommendation-section-title">おすすめポイント</h5>
                {/* DP-M-019: 編集したことを、色ではなく文字で示す (推薦 PDF には出さない) */}
                {override !== null && <span className="recommendation-reasons-badge">編集済み</span>}
                {!editing && override !== null && (
                    <button
                        type="button"
                        className="btn-row btn-row--ghost"
                        onClick={handleRevert}
                        disabled={locked || saving}
                    >
                        AI の文に戻す
                    </button>
                )}
                {!editing && (
                    <button
                        ref={editButtonRef}
                        type="button"
                        className="btn-row btn-row--ghost"
                        aria-label="おすすめポイントを編集"
                        onClick={() => onStartEdit?.()}
                        disabled={locked || saving}
                    >
                        編集
                    </button>
                )}
                {/* 領域ごと出し入れすると読み上げられないことがあるため、role="status" は常に置く */}
                {!editing && (
                    <span role="status" className="recommendation-reasons-status">
                        {saving && '保存中...'}
                    </span>
                )}
            </div>

            {editing ? (
                <div className="recommendation-reasons-editor">
                    <textarea
                        ref={textareaRef}
                        className="recommendation-reasons-input"
                        aria-label={`おすすめポイント（${REASONS_MAX_LENGTH} 文字以内）`}
                        aria-describedby={counterId}
                        rows={5}
                        value={text}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        disabled={saving}
                    />
                    <div className="recommendation-reasons-edit-row">
                        <button
                            type="button"
                            className="btn-row btn-row--primary"
                            onClick={handleSave}
                            disabled={saving || saveLocked}
                        >
                            保存
                        </button>
                        <button
                            type="button"
                            className="btn-row btn-row--ghost"
                            onClick={() => onCancelEdit?.()}
                            disabled={saving}
                        >
                            キャンセル
                        </button>
                        <span id={counterId} className="recommendation-reasons-counter tabular-nums">
                            {countReasonsLength(text)} / {REASONS_MAX_LENGTH} 文字
                        </span>
                        <span role="status" className="recommendation-reasons-status">
                            {saving && '保存中...'}
                        </span>
                    </div>
                </div>
            ) : displayed !== null ? (
                <div className="recommendation-reason-chip">
                    <span aria-hidden="true">✓</span>
                    <span className="recommendation-reasons-text">{displayed}</span>
                </div>
            ) : (
                <p className="matching-filter-empty">{REASONS_NOT_GENERATED_TEXT}</p>
            )}

            {alertMessage && (
                <span className="recommendation-reasons-error" role="alert">{alertMessage}</span>
            )}
        </div>
    );
}

export default RecommendationReasons;
